/**
 * 装配体标准配合预览。
 *
 * 文件职责：
 * 1. 将当前候选 Mate Definition 投入独立求解器；
 * 2. 输出预览 transform 与诊断状态；
 * 3. 不保存 UI 状态，也不直接修改场景对象。
 */

import {
  StandardMateSolver,
  type AssemblyMateSolveDiagnostic,
  type StandardMateSolverInput,
} from '../solver';
import { MateSolveStatus, cloneComponentTransformSnapshot } from '../definition';
import type { ComponentTransformSnapshot } from '../component-instance';

/**
 * 配合预览结果。
 */
export interface AssemblyMatePreviewResult {
  /** 预览求解状态。 */
  readonly status: MateSolveStatus;
  /** 预览后的组件 transform。 */
  readonly componentTransforms: Readonly<Record<string, ComponentTransformSnapshot>>;
  /** 预览前组件 transform，用于取消时恢复。 */
  readonly restoreTransforms: Readonly<Record<string, ComponentTransformSnapshot>>;
  /** 单条配合诊断。 */
  readonly diagnostics: readonly AssemblyMateSolveDiagnostic[];
}

/**
 * 克隆组件 transform 映射。
 *
 * @param transforms 源 transform 映射。
 * @returns transform 副本。
 */
function cloneTransformMap(
  transforms: Readonly<Record<string, ComponentTransformSnapshot>>,
): Record<string, ComponentTransformSnapshot> {
  const result: Record<string, ComponentTransformSnapshot> = {};
  Object.entries(transforms).forEach(([componentId, transform]) => {
    result[componentId] = cloneComponentTransformSnapshot(transform);
  });
  return result;
}

/**
 * 从求解输入创建恢复快照。
 *
 * @param input 求解输入。
 * @returns 恢复 transform 映射。
 */
function createRestoreTransforms(
  input: StandardMateSolverInput,
): Record<string, ComponentTransformSnapshot> {
  const result: Record<string, ComponentTransformSnapshot> = {};
  Object.values(input.components).forEach((component) => {
    result[component.componentInstanceId] = cloneComponentTransformSnapshot(component.transform);
  });
  return result;
}

/**
 * 创建标准配合预览。
 *
 * @param input 求解输入。
 * @param solver 可注入求解器，便于测试替换。
 * @returns 预览结果。
 */
export function createAssemblyMatePreview(
  input: StandardMateSolverInput,
  solver: StandardMateSolver = new StandardMateSolver(),
): AssemblyMatePreviewResult {
  if (input.mateDefinitions.length === 0) {
    return {
      status: MateSolveStatus.NotRun,
      componentTransforms: createRestoreTransforms(input),
      restoreTransforms: createRestoreTransforms(input),
      diagnostics: [],
    };
  }

  const result = solver.solve(input);
  return {
    status: result.status,
    componentTransforms: cloneTransformMap(result.componentTransforms),
    restoreTransforms: createRestoreTransforms(input),
    diagnostics: result.diagnostics.map((diagnostic) => ({ ...diagnostic })),
  };
}
